<template>
  <button
    :data-testid="`vehicle-offer-${offer.car.id}`"
    class="flex gap-4 rounded-lg bg-white p-4 text-left transition duration-300"
    :class="{ 'opacity-50': !offer.isAvailable }"
  >
    <div class="basis-[55%]">
      <p class="text-base font-semibold leading-tight">
        {{ offer.car.vehicleType!.make.name }} {{ offer.car.vehicleType!.model }}
      </p>
      <div class="mt-1 flex flex-wrap items-center gap-2">
        <LicencePlate v-if="offer.car.licencePlate && offer.car.licencePlate !== '-'">{{
          offer.car.licencePlate
        }}</LicencePlate>
        <span class="text-xs text-obsidian-500">{{ offer.car.fleetNumber }}</span>
      </div>
      <p class="text-xs leading-tight">
        {{
          getLocalizedField(
            getOperationalStationOfCar(offer.car as unknown as Car, startDate, stationIds).info,
            locale,
          )
        }}
      </p>
      <img v-if="imageUrl" :src="imageUrl" class="mt-4" />
    </div>
    <div class="basis-[45%]">
      <p class="mb-3 mt-1 font-semibold">
        {{
          offer.pricing.pricePerExtraKm
            ? t('inclKm', { inclusiveKm: offer.pricing.inclusiveKm })
            : t('unlimitedInclKm')
        }}
      </p>
      <p>{{ power }} {{ t('hp') }}</p>
      <p class="mb-3">{{ seats }}-{{ t('seater') }}</p>
      <template v-if="isLongTerm">
        <p class="text-base font-semibold text-primary">
          <span v-currency="monthlyAvg" /> / {{ t('month') }}
        </p>
        <p class="text-sm text-black/30"><span v-currency="dailyAvg" /> / {{ t('day') }}</p>
        <p class="text-sm text-black/30"><span v-currency="total" /> {{ t('total') }}</p>
      </template>
      <template v-else>
        <p class="text-base font-semibold text-primary">
          <span v-currency="dailyAvg" /> / {{ t('day') }}
        </p>
        <p class="text-sm text-black/30"><span v-currency="total" /> {{ t('total') }}</p>
      </template>
    </div>
  </button>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Offer } from '@carvia/ros-client-types';
import { getOperationalStationOfCar } from '../../../../hooks/use-current-station-of-car';
import type { Car } from '../../../../entities/cars/car.entity';
import { getLocalizedField } from '@/utils/get-localized-field';
import { getCarTotalSeats } from '@/utils/seats';
import LicencePlate from '@/components/LicencePlate.vue';
import {
  getAverageMonthlyPrice,
  getAveragePricePerDay,
  getContractTotal,
  isLongTermPricing,
} from '@/utils/pricing.utils';

const props = defineProps<{
  offer: Offer;
  startDate: Date;
  stationIds?: string[];
}>();

const { t, locale } = useI18n();

const imageUrl = computed(
  () => props.offer.car.imageUrl ?? props.offer.car.vehicleType!.imageUrl ?? undefined,
);

const power = computed(() => props.offer.car.power ?? props.offer.car.vehicleType!.power);
const seats = computed(() => getCarTotalSeats(props.offer.car as unknown as Car));
const isLongTerm = computed(() => isLongTermPricing(props.offer.pricing));
const dailyAvg = computed(() => getAveragePricePerDay(props.offer.pricing));
const monthlyAvg = computed(() => getAverageMonthlyPrice(props.offer.pricing));
const total = computed(() => getContractTotal(props.offer.pricing));
</script>

<i18n lang="json">
{
  "en": {
    "inclKm": "Incl. {inclusiveKm} km",
    "unlimitedInclKm": "Unlimited incl. km",
    "hp": "hp",
    "seater": "seater",
    "day": "day",
    "month": "month",
    "total": "total"
  },
  "de": {
    "inclKm": "Inkl. {inclusiveKm} km",
    "unlimitedInclKm": "Unbegrenzt Inkl. km",
    "hp": "PS",
    "seater": "Sitzer",
    "day": "Tag",
    "month": "Monat",
    "total": "Gesamt"
  },
  "es": {
    "inclKm": "Incl. {inclusiveKm} km",
    "unlimitedInclKm": "Km incluidos ilimitados",
    "hp": "CV",
    "seater": "plazas",
    "day": "día",
    "month": "mes",
    "total": "total"
  }
}
</i18n>
